import { useEffect, useContext, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
// import { FaroRoutes } from '@grafana/faro-react';


import Sandcastle from "@/Apps/Sandcastle";
import Sandbox from "@/Apps/Sandcastle/Sandbox";
import { KitchenSink, LeftSidebar } from "@/ui/Layout";

import {
  Components,
  Elements,
  Charts,
  Widgets,
  Icons,
  DragDrop,
  Tables,
  Forms,
  Pages,
} from "@/ui/Layout/KitchenSink";

import Login from "@/Login";

import { LegacyTab, UnifiedSettings } from "@/Settings/UnifiedSettings";
import { Profile } from "@/Settings/Profile";
import { ConnectClaude } from "@/Apps/Sites/ConnectClaude";
import { DomainsDashboard } from "@/Settings/DomainManagement";
import { Integrations } from "@/Settings/IntegrationManagement/Integrations";
import Launchpad from "@/Apps/Launchpad";

import Test, { TestChat } from "@/Test";

import { HookTriggers, ListItemsHooks } from "./plugins/types";
import { triggerAsyncHook } from "./plugins/client";
import Onboarding from "./Login/Onboarding";
import ForgotPassword from "./Login/ForgotPassword";
import ResetPassword from "./Login/ResetPassword";
import { Scroll } from "./Apps/PFOC/Scroll";
import { AppContext } from "./AppContext";
import Setup from "./Login/Setup";

import CmdK from "./ui/CommunicationView/CmdK";

import { ClerkLogin } from "./Login/ClerkLogin";
import { ClerkVerify } from "./Login/ClerkVerify";
import { SignInGate } from "./Login/SignInGate";
import { ShareAppConfirmation, ShareCampaignConfirmation } from "./Settings/CampaignSharing";
import { Copilot } from "./Apps/AITools/Copilot";
import { CopilotWrapper } from "./Apps/AITools/CopilotWrapper";
import { Browsers } from "@phosphor-icons/react";
import { TreeStructure } from "@phosphor-icons/react";
import { ChatBot } from "./Apps/AITools/ChatBot";
import { NewspaperClipping } from "@phosphor-icons/react";
import { OfflineChatBot } from "./Apps/AITools/OfflineChatBot";
import { LangGraphBot } from "./Apps/AITools/LangGraphBot/LangGraphBot";
import { CampaignBuilderArtifact } from "./Apps/AITools/LangGraphBot/components/CampaignBuilderArtifact";
import { PageBuilderArtifact } from "./Apps/AITools/LangGraphBot/components/PageBuilderArtifact";
// import { AddComponentTool } from "./Apps/AITools/LangGraphBot/components/ChangedComponentTool";
import { NavGroupItem } from "./ui/Layout/LeftSidebar/types";
import { AdminBanner } from "./Apps/Admin/AdminBanner";
import { SharedReview } from "@/Apps/Reviews/SharedReview";
import { MyReviewRoom, MyReviews } from "@/Apps/Reviews/MyReviews";

const AppRoutesFromPlugins = () => {
  const [routes, setRoutes] = useState([]);

  const contextObj = useContext(AppContext);

  const loadInAllAppRoutes = async () => {
    const routes = await triggerAsyncHook(
      HookTriggers.onListItems,
      {
        id: ListItemsHooks.ROUTES,
      },
      null,
      null,
      contextObj
    );

    setRoutes(routes);
  };

  useEffect(() => {
    loadInAllAppRoutes();
  }, [contextObj]);

  return routes.map(({ path, element, children = [] }, key) => (
    <Route key={key} path={path}>
      <Route index element={element} />
      {children.map(({ path, element }, key) => (
        <Route key={key} path={path} element={element} />
      ))}
    </Route>
  ));
};

// Pages anyone can open without signing in. The sign-in check (SignInGate) sends a signed-out
// visit to any OTHER page to the login screen, remembering the page (LNK-1) — so a new public page
// goes in this list, or signed-out visitors get bounced to login.
const PUBLIC_ROUTES = [
  { path: "/setup/:action/:token/:prompt/:settings", element: <Setup /> },
  { path: "/authLogin", element: <ClerkLogin /> },
  { path: "/authVerify", element: <ClerkVerify /> },
  { path: "/login", element: <Login /> },
  { path: "/r/:token", element: <SharedReview /> }, // RV-7 — a review opened from its share link: no sign-in
  { path: "/my/:code", element: <MyReviews /> }, // RV-4 — "My reviews": one private link per account, for the phone's home screen
  { path: "/my/:code/:id", element: <MyReviewRoom /> },
  { path: "/forgot-password", element: <ForgotPassword /> },
  { path: "/forgot-password.html", element: <Navigate to="/forgot-password" /> },
  { path: "/reset-password/:token", element: <ResetPassword /> },
  { path: "/login/:name", element: <Login email="demo@10minutefunnels.com" password="demo123" /> },
  { path: "/setup/:action/:token/:prompt", element: <Setup /> },
  { path: "/onboarding", element: <Onboarding /> },
  { path: "/", element: <Navigate to="/login" /> },
  { path: "/pfoc-scroll", element: <Scroll /> },
  { path: "/copilot", element: <OfflineChatBot /> },
];
const PUBLIC_PATHS = PUBLIC_ROUTES.map(({ path }) => path);

export const AppRoutes = () => {
  return (
    <>
      <AdminBanner />
      <Routes>
        {PUBLIC_ROUTES.map(({ path, element }) => (
          <Route key={path} path={path} element={element} />
        ))}
        {/* <Route path="/" element={<Navigate to="/authLogin" />} /> */}
        {/* <Route path="/ui">
            <Route index element={<KitchenSink />} />
            <Route path="components" element={<Components />} />
            <Route path="elements" element={<Elements />} />
            <Route path="charts" element={<Charts />} />
            <Route path="widgets" element={<Widgets />} />
            <Route path="forms" element={<Forms />} />
            <Route path="pages" element={<Pages />} />
          </Route> */}
      </Routes>

      {/* </SignedOut> */}
      <SignInGate publicPaths={PUBLIC_PATHS}>
        <Routes>
          {/* <Route path="/sandcastle" element={<Sandcastle />} /> */}
          {/* <Route path="/sandcastleIDE" element={<Sandbox />} /> */}
          <Route path="/launchpad">
            <Route index element={<Launchpad type="all" />} />
            <Route path="funnels" element={<Launchpad type="funnels" />} />
            <Route path="pages">
              <Route index element={<Launchpad type="pages" />} />
              <Route path=":id" element={<Launchpad type="pages" />} />
            </Route>
            <Route path="sites">
              <Route index element={<Launchpad type="sites" />} />
              <Route path=":id" element={<Launchpad type="sites" />} />
            </Route>
            <Route path="forms">
              <Route index element={<Launchpad type="forms" />} />
              <Route path=":id" element={<Launchpad type="forms" />} />
            </Route>
            <Route path="apps">
              <Route index element={<Launchpad type="apps" />} />
              <Route path=":appId" element={<Launchpad type="apps" />} />
            </Route>
          </Route>
          {/* <Route path="/kitchenSink" element={<KitchenSink />} /> */}
          <Route path="/profile" element={<LegacyTab path="/profile" legacy={<Profile />} />} />
          <Route path="/settings" element={<UnifiedSettings />} />
          <Route path="/domains" element={<LegacyTab path="/domains" legacy={<DomainsDashboard />} />} />
          <Route path="/integrations" element={<LegacyTab path="/integrations" legacy={<Integrations />} />} />
          {/* Open to every signed-in user (the Sites plugin route is gated by the sites permission, which Agents users lack). */}
          <Route path="/connect-claude" element={<LegacyTab path="/connect-claude" legacy={<ConnectClaude />} />} />
          <Route path="/test" element={<Test />} />
          {/* <Route path="/test-chat" element={<TestChat />} /> */}
          {/* <Route path="/editor-test" element={<Full />} /> */}
          <Route
            path="/share/:campaignId"
            element={<ShareCampaignConfirmation />}
          />
          {AppRoutesFromPlugins()}
        </Routes>
      </SignInGate>
    </>
  );
};
